import { useEffect, useRef, useState } from "react";

interface UseInViewOptions {
  /**
   * Root element for intersection observer. Defaults to viewport
   */
  root?: Element | null;
  /**
   * Margin around root. Can be similar to CSS margin property
   * Example: "10px 20px 30px 40px" (top, right, bottom, left)
   */
  rootMargin?: string;
  /**
   * Either a single number or an array of numbers between 0.0 and 1.0
   * Specifies at what percentage of the target's visibility the observer callback should execute
   */
  threshold?: number | number[];
  /**
   * If true, observer will disconnect after first intersection
   */
  triggerOnce?: boolean;
  /**
   * Delay in milliseconds before setting inView to true
   * Useful for preventing fetches when user scrolls quickly past items
   * Default: 0 (no delay)
   */
  delay?: number;
}

/**
 * Hook to track if an element is in the viewport using Intersection Observer
 * Useful for lazy loading content, triggering animations, or conditional data fetching
 *
 * @param options - Configuration options for the Intersection Observer
 * @returns A tuple containing the ref to attach to the element and a boolean indicating if it's in view
 *
 * @example
 * ```tsx
 * // Basic usage
 * const [ref, inView] = useInView({ threshold: 0.5, rootMargin: '100px' });
 *
 * // With delay to prevent fast-scroll fetches
 * const [ref, inView] = useInView({
 *   triggerOnce: true,
 *   delay: 150
 * });
 *
 * return (
 *   <div ref={ref}>
 *     {inView ? 'Visible!' : 'Not visible'}
 *   </div>
 * );
 * ```
 */
export const useInView = <T extends Element = HTMLDivElement>({
  root = null,
  rootMargin = "200px",
  threshold = 0,
  triggerOnce = false,
  delay = 0,
}: UseInViewOptions = {}): [React.RefObject<T>, boolean] => {
  const elementRef = useRef<T>(null);
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Skip if already triggered and triggerOnce is enabled
    if (triggerOnce && inView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;

        // Clear any pending timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        if (isIntersecting && delay > 0) {
          // Set a timeout to delay setting inView to true
          timeoutRef.current = setTimeout(() => {
            setInView(true);

            // Disconnect if triggerOnce
            if (triggerOnce && observerRef.current) {
              observerRef.current.disconnect();
            }
          }, delay);
        } else if (isIntersecting) {
          // No delay, set immediately
          setInView(true);

          // Disconnect if triggerOnce and now in view
          if (triggerOnce && observerRef.current) {
            observerRef.current.disconnect();
          }
        } else {
          // Not intersecting anymore, set to false
          setInView(false);
        }
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    observerRef.current = observer;
    observer.observe(element);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [root, rootMargin, threshold, triggerOnce, inView, delay]);

  return [elementRef, inView];
};
